import React from 'react'
import { Grid, Stack, Typography, useTheme, SelectChangeEvent } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { useApp } from 'src/Hooks'
import { NumberField, Select } from 'src/Libs/inputs/react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DynamicTariffSettingsFormFields, SurchargeUnit } from 'src/Libs/dynamic-tariff-settings/constants'
import { UnitTypes } from 'src/Libs/types'
import { PowerPriceContainer } from './PowerPriceContainer'

interface Props {
  disabled?: boolean
  depth?: number
  hasWritingRights: boolean
}

/**
 * component to show and edit dynamic power price of dynamic tariff settings form
 */
const DynamicPowerPriceSettingsForm: React.FC<Props> = ({ disabled = false, depth = 0, hasWritingRights }) => {
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const { isMobile: isDownSm } = useApp()
  const backgroundColorEnabled = depth % 2 === 0 ? theme.palette.grey.grey07 : theme.palette.background.paper
  const { getValues, setValue, clearErrors } = useFormContext()
  const surchargeUnit = useWatch({ name: DynamicTariffSettingsFormFields.SurchargeUnit })
  const surchargeFieldName =
    surchargeUnit === SurchargeUnit.CtPerKwh ? DynamicTariffSettingsFormFields.SurchargeValue : DynamicTariffSettingsFormFields.SurchargePercent

  /* carry the entered number into the newly active field and null the old one, so the displayed value stays in sync with form state */
  const handleUnitChange = (event: SelectChangeEvent<unknown>): void => {
    const isCtPerKwh = event.target.value === SurchargeUnit.CtPerKwh
    const previousFieldName = isCtPerKwh ? DynamicTariffSettingsFormFields.SurchargePercent : DynamicTariffSettingsFormFields.SurchargeValue
    const nextFieldName = isCtPerKwh ? DynamicTariffSettingsFormFields.SurchargeValue : DynamicTariffSettingsFormFields.SurchargePercent
    const carriedValue = getValues(previousFieldName) ?? null

    setValue(nextFieldName, carriedValue, { shouldDirty: true })
    setValue(previousFieldName, null, { shouldDirty: true })
    clearErrors([DynamicTariffSettingsFormFields.SurchargePercent, DynamicTariffSettingsFormFields.SurchargeValue])
  }

  return (
    <PowerPriceContainer sx={{ backgroundColor: disabled ? theme.palette.grey.grey06 : backgroundColorEnabled }}>
      <Grid container alignItems={'center'}>
        <Grid item xs={12} sm={8} md={6}>
          <Typography variant="body1" display="flex">
            {t('dynamic-tariff-settings.dynamic-power-price.surchargePercent')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={6}>
          <Stack flexDirection="row" justifyContent={isDownSm ? 'center' : 'end'} alignItems="center" gap={2}>
            <NumberField
              disabled={!hasWritingRights}
              sx={{ ...(isDownSm ? { width: '100%' } : { maxWidth: theme => theme.spacing(25) }) }}
              name={surchargeFieldName}
              inputProps={{ style: { textAlign: 'right' } }}
            />
            <Select
              name={DynamicTariffSettingsFormFields.SurchargeUnit}
              options={[
                { label: UnitTypes.percentage, value: SurchargeUnit.Percent },
                { label: UnitTypes.centsPerKiloWattHour, value: SurchargeUnit.CtPerKwh },
              ]}
              disabled={!hasWritingRights}
              onChange={handleUnitChange}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  height: theme => theme.shape.inputField.height,
                  paddingBlock: 0,
                  paddingInline: 2,
                  boxSizing: 'border-box',
                },
              }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={8} md={6}>
          <Typography variant="body1" display="flex">
            {t('dynamic-tariff-settings.dynamic-power-price.taxes')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={6}>
          <Stack flexDirection="row" justifyContent={isDownSm ? 'center' : 'end'} alignItems="center">
            <NumberField
              disabled={!hasWritingRights}
              sx={{ ...(isDownSm ? { width: '100%' } : { maxWidth: theme => theme.spacing(25) }) }}
              name={DynamicTariffSettingsFormFields.Taxes}
              suffix={UnitTypes.centsPerKiloWattHour}
              inputProps={{ style: { textAlign: 'right' } }}
            />
          </Stack>
        </Grid>
      </Grid>
    </PowerPriceContainer>
  )
}

export default DynamicPowerPriceSettingsForm
